/**
 * Le choix de ce qu'on regarde, puis le déboursé lui-même.
 *
 * Premier écran métier du produit. Il est en LECTURE SEULE, volontairement : ce
 * qu'il doit permettre, c'est d'ouvrir le classeur du déboursé à côté et de
 * vérifier les chiffres. La contre-étude modifiable — le vis-à-vis DEVIS |
 * CONTRE-ÉTUDE | ANALYSE du PRD §4.2 — vient après, et n'aurait aucun sens tant
 * que la fidélité du socle n'est pas constatée par le métier.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterNombre, STATUTS_CHANTIER } from "@planif/moteur";
import { lirePosition, retenirPosition } from "./position";
import type {
  BaremesTransport,
  ChantierListe,
  Generique,
  GroupeAdmin,
  ReponseComparaison,
} from "@planif/moteur";
import {
  affecterGroupe,
  ajouterLigne,
  chargerCarrieres,
  chargerChantiers,
  chargerMonCompte,
  chargerGroupes,
  deplacerLigne,
  chargerGeneriques,
  chargerVersion,
  deplacerUnBloc,
  creerChapitre,
  fixerProvenance,
  modifierLigne,
  nommerPoste,
  retenirPoste,
  seDeconnecter,
  supprimerChapitreCree,
} from "./api";
import type { Carriere, Compte, PlanningRecale, Role } from "./api";
import { Chantiers } from "./Chantiers";
import { Debourse } from "./Debourse";
import { Equipe } from "./Equipe";
import type { Modification } from "./Debourse";
import { Import } from "./Import";
import { Magasin } from "./Magasin";
import { Stocks } from "./Stocks";
import { Connexion, Inscription, NouveauMotDePasse } from "./Acces";
import { Membres } from "./Membres";
import { Parametrage } from "./Parametrage";
import { Sav } from "./Sav";
import { Soumissions } from "./Soumissions";
import { Suivi } from "./Suivi";
import { NouvelleVersion } from "./NouvelleVersion";
import { libelleChoix } from "./libelleVersion";
import type { Choix } from "./libelleVersion";
import { AnnulerValidation } from "./AnnulerValidation";
import { SupprimerVersion } from "./SupprimerVersion";
import { PlanGeneral } from "./PlanGeneral";
import { Planning } from "./Planning";
import { TableauxDeBord } from "./TableauxDeBord";
import { CHEMIN_FENETRE, PlanningRessources } from "./PlanningRessources";
import { FenetrePlanning } from "./FenetrePlanning";
import { FenetreAssistant } from "./FenetreAssistant";
import { CHEMIN_FENETRE_ASSISTANT } from "./adresseAssistant";
import {
  CHEMIN_FENETRE_PLANNING,
  adresseDuPlanningChantier,
  nomFenetrePlanning,
} from "./fenetreDuPlanning";
import { ecouterVersion, signalerVersionModifiee } from "./synchroVersion";
import { MESSAGE_ALLER_AU_PLAN } from "./cibles";
import type { CibleDeLaLigne, CibleDuPlanDeCharge } from "./cibles";
import { SaisieHeures } from "./SaisieHeures";
import { SaisieChauffeur } from "./SaisieChauffeur";
import { ValidationHeures } from "./ValidationHeures";

/**
 * Le libellé français d'un statut, ou le code s'il est inconnu.
 *
 * Un code inconnu s'affiche tel quel plutôt que de disparaître : c'est le signe
 * qu'une valeur est entrée en base sans passer par le vocabulaire du produit, et
 * un écran qui l'escamote empêche de s'en apercevoir.
 */
function libelleStatut(code: string): string {
  return code in STATUTS_CHANTIER
    ? STATUTS_CHANTIER[code as keyof typeof STATUTS_CHANTIER]
    : code;
}

type EtatListe =
  | { phase: "chargement" }
  | {
      phase: "chargee";
      chantiers: readonly ChantierListe[];
      /**
       * Les deux barèmes de zones (058), envoyés une fois pour toute la liste.
       *
       * La fiche du chantier y prend ses listes déroulantes ; les recopier sur chaque chantier
       * aurait mis la même vérité à trente endroits.
       */
      baremes: BaremesTransport;
      /**
       * Les sites actifs sans table de distances (08/09/2026).
       *
       * Envoyé une fois pour toute la liste, comme les barèmes : c'est un état du référentiel,
       * pas une propriété d'un chantier.
       */
      sitesSansTableDeDistances: number;
    }
  | { phase: "erreur"; raison: string };

type EtatVersion =
  | { phase: "vide" }
  | { phase: "chargement" }
  | { phase: "chargee"; reponse: ReponseComparaison }
  | { phase: "erreur"; raison: string };

function choixDuChantier(chantier: ChantierListe | undefined): Choix[] {
  if (chantier === undefined) return [];
  return chantier.depos.flatMap((depo) =>
    depo.versions.map((version) => ({ depo, version })),
  );
}


/**
 * Les écrans du produit.
 *
 * Pas de bibliothèque de navigation : à deux écrans, elle coûterait une
 * dépendance de plus à installer et à comprendre pour un `if`. Elle se
 * justifiera quand les adresses devront être partageables (« envoie-moi le lien
 * du chantier ») — c'est un besoin réel du §4.2, mais il n'est pas là
 * aujourd'hui.
 */
type Ecran =
  | "chantiers"
  | "contre-etude"
  | "stocks"
  | "planning"
  | "plan-general"
  | "tableaux-de-bord"
  | "import"
  | "parametrage"
  | "soumissions"
  | "membres"
  | "saisie-heures"
  | "validation-heures"
  | "chauffeur"
  | "sav";

const TITRES: Record<Ecran, string> = {
  chantiers: "Chantiers",
  "contre-etude": "Contre-étude",
  stocks: "Stocks",
  planning: "Planning",
  "plan-general": "Planning général",
  "tableaux-de-bord": "Tableaux de bord",
  import: "Importer un déboursé",
  parametrage: "Paramétrage",
  soumissions: "Soumissions",
  membres: "Membres",
  "saisie-heures": "Saisie des heures",
  "validation-heures": "Validation des heures",
  chauffeur: "Saisie chauffeur",
  sav: "SAV",
};

/**
 * Les écrans du TERRAIN : ceux qu'on tient en main autant qu'on les regarde.
 *
 * *Chris, 22/09/2026 : « les menus saisie des heures, validation des heures, saisie chauffeur,
 * stock et SAV doivent absolument être responsive mobile et écran PC. Tous les autres menus
 * sont des visuels sur écran. »*
 *
 * La liste sert à une seule chose : **garder à ces cinq-là le cadre qu'ils avaient**, pendant
 * que les neuf autres prennent toute la largeur de la dalle. Ils sont écrits pour le téléphone
 * d'abord — une colonne, des cibles larges, le magasinier debout avec un gant — et leurs
 * panneaux se centrent dans la page. Centrer une colonne de 36 rem au milieu de 2 560 px la
 * détacherait de son titre, resté en haut à gauche : ce qu'on gagnerait en largeur, on le
 * perdrait en lisibilité, sur les seuls écrans où la lisibilité prime sur la densité.
 *
 * Écrit ici et non en CSS parce que c'est une décision de PRODUIT — quel écran sert à qui — et
 * non une affaire de pixels. Un nouvel écran de terrain s'ajoute à cette liste ; le type
 * `Ecran` garantit qu'aucun nom n'y survivra à un renommage.
 */
const ECRANS_DU_TERRAIN: ReadonlySet<Ecran> = new Set<Ecran>([
  "saisie-heures",
  "validation-heures",
  "chauffeur",
  "stocks",
  "sav",
]);

/**
 * Les menus du statut BE (096) : le bureau d'études ne voit que ceux-là.
 *
 * *Chris, 27/10/2026 : « on ne coupe pas les routes, on ne met simplement pas pour le BE les
 * boutons d'accès ».* **Ce n'est pas une sécurité** : un écran caché reste joignable par l'API, et
 * seul le périmètre par chantier protège les affaires. C'est une application simplifiée — ce que
 * le BE fait se trouve dans ces quatre menus, le reste l'encombrait. Les chantiers servent à
 * rattacher l'APO, supprimer une étude et exporter.
 */
const MENUS_DU_BE: ReadonlySet<Ecran> = new Set<Ecran>([
  "import",
  "contre-etude",
  "planning",
  "chantiers",
]);

function ecranVisible(role: Role, ecran: Ecran): boolean {
  return role !== "be" || MENUS_DU_BE.has(ecran);
}

/** Les trois fonctions de validation, dans l'ordre des onglets du tableau de bord. */
type Fonction = "transport" | "exploitation" | "carrieres";

/**
 * Le code ERP d'une machine que l'adresse demande : `/materiel/04-02-01-102`.
 *
 * Le jumeau du chemin des articles, et pour la même raison : **l'adresse désigne l'objet,
 * jamais une fonction.** Une étiquette collée sur une pelle mènera donc au module SAV comme à
 * ce qui viendra après, sans qu'on la réimprime.
 */
function codeMaterielDemande(): string | undefined {
  const trouve = /^\/materiel\/(.+)$/.exec(window.location.pathname);
  return trouve?.[1] === undefined ? undefined : decodeURIComponent(trouve[1]);
}
/**
 * Le code article que l'adresse demande, s'il y en a un : `/article/1000001`.
 *
 * **C'est là qu'atterrit un QR code scané** (décision du 20/08/2026 : le QR encode une adresse et
 * non le code seul, pour qu'un téléphone ouvre quelque chose). Lu depuis `window.location` comme
 * les deux chemins publics — l'application n'a pas de routeur, et en ajouter un pour trois
 * chemins serait une dépendance pour un `if`.
 *
 * Le chemin reste valable une fois connecté : la porte ne le sert pas, elle sert l'application,
 * qui décide alors quel écran ouvrir.
 */
function codeArticleDemande(): string | undefined {
  const trouve = /^\/article\/(.+)$/.exec(window.location.pathname);
  return trouve?.[1] === undefined ? undefined : decodeURIComponent(trouve[1]);
}

function Application({
  moi,
  fonctions,
  onDeconnexion,
}: {
  moi: Compte;
  fonctions: readonly string[];
  onDeconnexion: () => void;
}): JSX.Element {
  const codeArticle = codeArticleDemande();
  const codeMateriel = codeMaterielDemande();
  // Un scan ouvre l'écran de l'objet scané, et non la contre-étude : c'est cet objet qu'on vient
  // chercher, personne ne scane une étiquette pour arriver ailleurs.
  /** Le menu est replié par défaut ; sous 900 px seulement, le CSS le cache réellement. */
  const [menuOuvert, setMenuOuvert] = useState(false);
  // Les deux écrans que visent ces chemins sont devenus des onglets du paramétrage : on ouvre
  // donc le paramétrage, qui choisit l'onglet d'après le même code. Les étiquettes déjà collées
  // sur les machines et dans les rayonnages continuent de mener au bon endroit.
  const voitLeMenu = (e: Ecran): boolean => ecranVisible(moi.role, e);
  const [ecran, setEcran] = useState<Ecran>(() => {
    if (codeArticle !== undefined || codeMateriel !== undefined) return "parametrage";
    // Actualiser ramène sur l'écran où l'on était (28/09/2026) — sauf un écran qu'on ne peut
    // plus ouvrir : les membres ne se montrent qu'à l'administrateur, et le BE n'a que ses quatre
    // menus (096).
    const retenu = lirePosition().ecran;
    if (
      retenu !== undefined &&
      retenu in TITRES &&
      (retenu !== "membres" || moi.role === "admin") &&
      ecranVisible(moi.role, retenu as Ecran)
    ) {
      return retenu as Ecran;
    }
    return "contre-etude";
  });
  /**
   * La journée que l'écran de saisie doit ouvrir, posée par la pile du valideur (084).
   *
   * Nulle = l'écran s'ouvre sur aujourd'hui et sur les chantiers où l'on est affecté, ce qui est
   * le cas du chef. Renseignée, elle désigne une journée d'un chantier où le valideur n'était pas
   * — le serveur n'ouvre alors que si le circuit l'y autorise.
   */
  const [cibleDeSaisie, setCibleDeSaisie] = useState<{
    chantierId: string;
    jour: string;
  } | null>(null);
  const [liste, setListe] = useState<EtatListe>({ phase: "chargement" });
  const [chantierId, setChantierId] = useState<string>("");
  const [versionId, setVersionId] = useState<string>("");
  const [version, setVersion] = useState<EtatVersion>({ phase: "vide" });
  /** La ligne dont la modification est en cours d'enregistrement, « poste#rang ». */
  const [enAttente, setEnAttente] = useState<string | null>(null);

  /* ---------------------------------------------------------------------------
   * Remonter du planning à la source (Chris, 17/09/2026)
   *
   * Deux sauts, et c'est ici qu'ils se rejoignent : l'un et l'autre changent d'ÉCRAN, ce
   * qu'aucun des écrans concernés ne sait faire. Le second change en plus de chantier et de
   * version — trois états qui vivent tous ici.
   * ------------------------------------------------------------------------- */

  /** Ce que le planning des ressources demande de montrer dans le plan de charge. */
  const [cibleDuPlan, setCibleDuPlan] = useState<CibleDuPlanDeCharge | null>(null);
  /** La ligne de contre-étude que le plan de charge demande d'ouvrir en mode planning. */
  const [cibleDeLaLigne, setCibleDeLaLigne] = useState<CibleDeLaLigne | null>(null);

  /**
   * Écouter la fenêtre du planning des ressources.
   *
   * **L'origine est vérifiée avant de lire quoi que ce soit.** N'importe quelle page peut
   * poster un message à une fenêtre qu'elle a ouverte ou qui l'a ouverte ; sans ce contrôle,
   * une page tierce ferait naviguer l'application sur des identifiants de son choix. Le message
   * est ensuite lu champ par champ : ce qui traverse une frontière de fenêtre est une donnée,
   * jamais un objet de confiance.
   */
  useEffect(() => {
    const ecouter = (evenement: MessageEvent): void => {
      if (evenement.origin !== window.location.origin) return;
      const donnee: unknown = evenement.data;
      if (typeof donnee !== "object" || donnee === null) return;
      const enveloppe = donnee as { type?: unknown; cible?: unknown };
      if (enveloppe.type !== MESSAGE_ALLER_AU_PLAN) return;
      const cible = enveloppe.cible as CibleDuPlanDeCharge | undefined;
      if (cible === undefined || typeof cible.jour !== "string") return;
      setCibleDuPlan(cible);
      setEcran("tableaux-de-bord");
    };
    window.addEventListener("message", ecouter);
    return () => {
      window.removeEventListener("message", ecouter);
    };
  }, []);
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  /**
   * Ce qu'une écriture a fait de plus que ce qu'on lui demandait.
   *
   * Aujourd'hui un seul cas : choisir un mode de livraison en contre-étude **fait naître une
   * pastille** dans le planning (migration 041). Le taire serait le pire des deux choix — le
   * conducteur découvrirait une livraison qu'il n'a pas posée et la prendrait pour un défaut,
   * ou ne la découvrirait pas du tout et croirait avoir encore à la placer.
   *
   * Séparé de `erreurSaisie` parce que ce n'est pas une erreur : le geste a réussi, il a
   * simplement fait plus que ce qu'on voyait.
   */
  const [avisSaisie, setAvisSaisie] = useState<string | null>(null);
  /**
   * Le référentiel, chargé une fois pour toutes les listes de choix.
   *
   * Il ne bouge pas pendant qu'on travaille une contre-étude, et le recharger à
   * chaque ouverture d'ouvrage ferait cent appels pour la même réponse. Il est
   * relu au changement d'écran, ce qui suffit : c'est là qu'on vient de le
   * modifier.
   */
  const [generiques, setGeneriques] = useState<readonly Generique[]>([]);
  /** Le référentiel des regroupements, pour les pastilles et les listes de choix. */
  const [groupes, setGroupes] = useState<readonly GroupeAdmin[]>([]);
  /** Les carrières, pour désigner la provenance d'un matériau du groupe. */
  const [carrieres, setCarrieres] = useState<readonly Carriere[]>([]);

  /**
   * (Re)charge la liste des chantiers.
   *
   * Relire plutôt que d'ajouter le chantier importé à la liste en mémoire : ce que
   * l'écran doit montrer, c'est ce que la base contient. Reconstituer côté
   * navigateur ce que le serveur vient d'écrire fabriquerait une seconde version
   * des faits, et un import à moitié refusé s'y afficherait comme réussi.
   *
   * La sélection courante n'est PAS écrasée : un import fait pendant qu'une
   * contre-étude est ouverte ne doit pas déplacer l'écran sous les yeux de celui
   * qui travaille dessus.
   */
  const rechargerChantiers = useCallback((): void => {
    chargerChantiers()
      .then(({ chantiers, baremes, sitesSansTableDeDistances }) => {
        setListe({ phase: "chargee", chantiers, baremes, sitesSansTableDeDistances });

        // **Le premier chantier s'ouvre, quel qu'en soit le nombre.**
        //
        // La condition était « un seul chantier en base » ; elle datait du jour où
        // il n'y en avait effectivement qu'un. Dès qu'il y en a eu trois, l'écran
        // s'est ouvert sur « — choisir — » et n'a plus rien affiché : ni déboursé,
        // ni totaux, ni bouton. Un écran vide alors que la base est pleine se lit
        // comme une panne, et c'est le métier qui le découvre.
        //
        // La liste arrive triée par n° d'affaire, donc « le premier » est stable
        // d'une ouverture à l'autre — et le sélecteur reste là pour en changer.
        //
        // Sauf à l'actualisation (28/09/2026) : le chantier et la version où l'on était
        // reviennent, s'ils existent encore — sinon le premier, comme avant.
        const premier = chantiers[0];
        setChantierId((actuel) => {
          if (actuel !== "") return actuel;
          const retenue = lirePosition();
          const chantierRetenu = chantiers.find((c) => c.id === retenue.chantierId);
          if (chantierRetenu !== undefined) {
            const choix = choixDuChantier(chantierRetenu);
            const versionRetenue =
              choix.find((c) => c.version.id === retenue.versionId) ?? choix[0];
            if (versionRetenue !== undefined) setVersionId(versionRetenue.version.id);
            return chantierRetenu.id;
          }
          if (premier === undefined) return actuel;
          const premierChoix = choixDuChantier(premier)[0];
          if (premierChoix !== undefined) setVersionId(premierChoix.version.id);
          return premier.id;
        });
      })
      .catch((erreur: unknown) => {
        setListe({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  }, []);

  useEffect(rechargerChantiers, [rechargerChantiers]);

  /*
    Retenir où l'on est, pour qu'une actualisation y ramène (28/09/2026, voir `position.ts`).
    Le chantier et la version ne s'écrivent qu'une fois choisis : l'état vide du démarrage
    effacerait sinon la position qu'on s'apprête à relire.
  */
  useEffect(() => {
    retenirPosition({ ecran });
  }, [ecran]);
  useEffect(() => {
    if (chantierId !== "" && versionId !== "") retenirPosition({ chantierId, versionId });
  }, [chantierId, versionId]);

  /*
    Le défilement de la page : retenu en continu, rendu UNE fois, quand l'écran rouvert a fini
    de charger. Rendu plus tôt, il buterait sur une page encore trop courte pour défiler.
  */
  const [defilementRendu, setDefilementRendu] = useState(false);
  useEffect(() => {
    const noter = (): void => {
      retenirPosition({ defilement: window.scrollY });
    };
    window.addEventListener("scroll", noter, { passive: true });
    return () => {
      window.removeEventListener("scroll", noter);
    };
  }, []);
  const pageChargee =
    ecran === "contre-etude" || ecran === "planning"
      ? version.phase === "chargee"
      : liste.phase === "chargee";
  useEffect(() => {
    if (defilementRendu || !pageChargee) return;
    setDefilementRendu(true);
    const voulu = lirePosition().defilement ?? 0;
    if (voulu <= 0) return;
    // Deux images : la première pose le rendu, la seconde le trouve mis en page.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, voulu);
      });
    });
  }, [defilementRendu, pageChargee]);

  useEffect(() => {
    if (versionId === "") {
      setVersion({ phase: "vide" });
      return;
    }

    let abandonne = false;
    setVersion({ phase: "chargement" });

    chargerVersion(versionId)
      .then((reponse) => {
        if (abandonne) return;
        setVersion({ phase: "chargee", reponse });
      })
      .catch((erreur: unknown) => {
        if (abandonne) return;
        setVersion({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });

    // Sans ce drapeau, la réponse d'une version qu'on vient de quitter écrase
    // celle qu'on est en train de regarder — d'autant plus facilement que la
    // première est plus lente que la seconde.
    return () => {
      abandonne = true;
    };
  }, [versionId]);

  /**
   * Relire la version **sans vider l'écran** — ce que l'autre fenêtre vient de changer.
   *
   * *Chris, 22/09/2026 : « si je modifie dans le planning cela se mettra à jour dans la
   * contre-étude sans la fermer ».*
   *
   * Distinct de l'effet ci-dessus, qui passe par `phase: "chargement"` : celui-là répond à un
   * changement de version, où il faut cesser d'afficher l'ancienne. Ici la version est la même,
   * seul son contenu a bougé — remplacer le tableau par « Chargement du déboursé… » à chaque
   * barre déplacée sur le deuxième écran ferait clignoter l'écran qu'on est en train de lire.
   *
   * Une erreur de relecture ne casse pas l'affichage non plus, et c'est voulu : ce qui est à
   * l'écran reste juste jusqu'à preuve du contraire, et l'écran de la modification, lui, dira
   * ce qui ne va pas.
   */
  const relireVersionAilleurs = useCallback((): void => {
    if (versionId === "") return;
    chargerVersion(versionId)
      .then((reponse) => {
        setVersion({ phase: "chargee", reponse });
      })
      .catch(() => undefined);
  }, [versionId]);

  /*
    L'abonnement au canal des deux fenêtres. Voir `synchroVersion.ts` : rien n'y transite qu'un
    identifiant de version, et c'est le serveur qui rend les chiffres.
  */
  useEffect(() => ecouterVersion(versionId, relireVersionAilleurs), [versionId, relireVersionAilleurs]);

  // Le référentiel est relu à chaque retour sur la contre-étude : c'est là
  // qu'on vient éventuellement de le modifier dans l'autre écran.
  useEffect(() => {
    if (ecran !== "contre-etude") return;
    let abandonne = false;
    chargerGeneriques()
      .then(({ generiques: liste }) => {
        if (!abandonne) setGeneriques(liste);
      })
      // Un référentiel injoignable ne doit pas empêcher de LIRE la contre-étude :
      // seules les listes de choix en dépendent, et l'erreur se verra au
      // premier remplacement tenté.
      .catch(() => undefined);
    // Les regroupements sont relus au même moment et pour la même raison : on
    // vient peut-être d'ajouter un mot-clé ou de recolorer une famille.
    chargerGroupes()
      .then(({ groupes: liste }) => {
        if (!abandonne) setGroupes(liste);
      })
      .catch(() => undefined);
    // Et les carrières, pour la liste de provenance des matériaux du groupe : on
    // vient peut-être d'en ajouter une dans l'écran des référentiels.
    chargerCarrieres()
      .then(({ carrieres: liste }) => {
        if (!abandonne) setCarrieres(liste);
      })
      .catch(() => undefined);
    return () => {
      abandonne = true;
    };
  }, [ecran]);

  /**
   * Enregistrer une modification, puis **tout recharger depuis le serveur**.
   *
   * Recalculer localement serait plus rapide à l'affichage et faux à la
   * première divergence : changer une ligne modifie le coût de son ouvrage, de
   * son chapitre, de tous les chapitres au-dessus, et du déboursé. Le serveur
   * refait tourner le moteur sur l'état réel de la base — c'est la seule
   * version des chiffres qui ne puisse pas mentir.
   */
  const appliquer = useCallback(
    (modification: Modification): void => {
      const cle = `${modification.poste}#${modification.rang}`;
      setEnAttente(cle);
      setErreurSaisie(null);
      setAvisSaisie(null);

      const {
        poste,
        rang,
        ajouterCode,
        deplacer,
        retenu,
        groupeId,
        provenance,
        libelle,
        creerChapitre: veutChapitre,
        supprimerChapitre: veutRetirerChapitre,
        ...valeurs
      } = modification;
      // Neuf gestes distincts côté serveur : affecter une famille de travaux, écarter ou
      // reprendre un POSTE, créer une ligne, en déplacer une, dire d'où vient un matériau, en
      // modifier une, et — depuis le 21/08/2026 — créer un chapitre absent du devis, le
      // nommer, le retirer. Les confondre obligerait une seule route à deviner lequel on
      // veut — et la provenance, qui ne change aucun montant, passerait par le chemin qui
      // marque la ligne comme retravaillée.
      const envoi =
        veutChapitre === true
          ? creerChapitre(versionId)
          : veutRetirerChapitre === true
            ? supprimerChapitreCree(versionId, poste)
            : libelle !== undefined
              ? nommerPoste(versionId, poste, libelle)
              : groupeId !== undefined
                ? affecterGroupe(versionId, poste, groupeId)
                : retenu !== undefined
                  ? retenirPoste(versionId, poste, retenu)
                  : provenance !== undefined
                    ? fixerProvenance(versionId, poste, rang, provenance)
                    : ajouterCode !== undefined
                      ? ajouterLigne(versionId, poste, ajouterCode, valeurs.quantite ?? "0")
                      : deplacer !== undefined
                        ? deplacerLigne(versionId, poste, rang, deplacer)
                        : modifierLigne(versionId, poste, rang, valeurs);

      envoi
        .then((ecrit) => {
          // La seule écriture qui en entraîne une autre : un mode de livraison choisi en
          // contre-étude pose la pastille du planning (041). On lit la réponse plutôt que de
          // recharger et comparer — le serveur sait ce qu'il a fait, l'écran n'a pas à le
          // déduire.
          const posee =
            ecrit !== null &&
            typeof ecrit === "object" &&
            "livraisonPosee" in ecrit &&
            ecrit.livraisonPosee !== null
              ? (ecrit.livraisonPosee as { jourRelatif: number; quantite: string })
              : null;
          // Les avis se CUMULENT : une même écriture peut poser une pastille et conserver le
          // prix du devis. Écraser l'un par l'autre ferait disparaître, au hasard de l'ordre,
          // l'explication de ce qui vient d'arriver au chiffrage.
          const avis: string[] = [];
          if (posee !== null) {
            avis.push(
              `Une livraison de ${posee.quantite} a été placée au J${String(posee.jourRelatif)} dans le planning. ` +
                "Le jour, le volume et l'heure limite se corrigent là-bas.",
            );
          }
          // Les pastilles déjà posées qui ne portaient aucun acheminement viennent de recevoir
          // celui de la ligne. Même raison de l'annoncer : ce qui change sans clic doit se dire.
          const reprises =
            ecrit !== null &&
            typeof ecrit === "object" &&
            "pastillesReprises" in ecrit &&
            typeof ecrit.pastillesReprises === "number"
              ? ecrit.pastillesReprises
              : 0;
          if (posee === null && reprises > 0) {
            avis.push(
              reprises === 1
                ? "Une livraison déjà placée au planning a reçu cet acheminement."
                : `${String(reprises)} livraisons déjà placées au planning ont reçu cet acheminement.`,
            );
          }
          /*
            **LE SITE A ÉTÉ PROPOSÉ, ET SURTOUT UN PRIX EST ARRIVÉ AVEC LUI.**

            Cocher « groupe » sur un matériau de carrière désigne le site le plus proche
            (08/09/2026). Ce n'est pas un champ neutre : un site désigné pose le prix de sa
            grille tarifaire sur la ligne — donc **le déboursé change sans qu'on ait choisi de
            carrière**. Sans cette phrase, ce serait le pire de ce que ce produit évite : un
            euro qui bouge en silence.

            L'avis dit aussi qu'il est modifiable — *« on devra pouvoir modifier le choix
            manuellement bien entendu »* (Chris) — parce qu'une valeur apparue toute seule se
            lit facilement comme une valeur imposée.
          */
          const sitePropose =
            ecrit !== null &&
            typeof ecrit === "object" &&
            "sitePropose" in ecrit &&
            ecrit.sitePropose === true;
          if (sitePropose) {
            avis.push(
              "Le site le plus proche du chantier a été renseigné, et son prix de grille " +
                "appliqué. Choisir un autre site dans la liste le remplace.",
            );
          }
          /*
            **Le prix n'a pas bougé, et il faut le dire.**

            *« Uniquement pour les articles qui commencent par 100 »* (métier, 21/08/2026) : un
            article que la grille ne tarife pas garde le prix du devis. Sans cette phrase,
            désigner un site sur un article n'aurait aucun effet visible — et on ne saurait pas
            si la grille a été consultée. C'est le pendant exact du « à 0 » des matériaux.
          */
          const tarif =
            ecrit !== null && typeof ecrit === "object" && "tarif" in ecrit
              ? (ecrit.tarif as { etat?: string } | null)
              : null;
          if (tarif?.etat === "prix_conserve") {
            avis.push(
              "Cet article n'est pas dans la grille tarifaire des carrières : le prix du devis " +
                "est conservé, il n'y a rien à renseigner.",
            );
          }
          /*
            **La trame créée dit où elle est.** Elle se place à la suite du dernier chapitre,
            donc en bas d'un arbre qui compte souvent plus de cent lignes : sans cette phrase,
            un clic qui ajoute un poste hors du champ de vision se lit comme un clic sans
            effet. C'est exactement l'anomalie du 21/08 sur les pastilles posées au J20.
          */
          const trame =
            ecrit !== null && typeof ecrit === "object" && "sousChapitre" in ecrit
              ? (ecrit as { chapitre: string; sousChapitre: string })
              : null;
          if (trame !== null) {
            avis.push(
              `Le chapitre ${trame.chapitre} et son sous-chapitre ${trame.sousChapitre} sont ` +
                "créés en bas de l'arbre. Les nommer, puis y ajouter les ressources et les articles.",
            );
          }
          /*
            **Le planning a suivi les heures corrigées, et il faut le dire.**

            *Anomalie de Chris, 25/08/2026 : « au 2.2 les quantités heure chauffeur ont été
            modifiées par Régis de 22,5 à 18, et dans le planning tu as posé les heures du
            devis chiffré ».* Le recalage est fait par le serveur, dans la même transaction.
            Ce qui manquerait sans cette phrase, c'est de savoir qu'il a eu lieu : la
            correction porte sur un écran qu'on n'a pas sous les yeux, et une pastille qui
            change en silence est exactement ce qu'on ne veut pas.
          */
          const recale =
            ecrit !== null && typeof ecrit === "object" && "pastillesAjustees" in ecrit
              ? (ecrit as PlanningRecale)
              : null;
          const pastilles = recale?.pastillesAjustees ?? null;
          if (pastilles !== null) {
            const bouge = pastilles.modifiees.length;
            const retire = pastilles.retirees.length;
            const seule = pastilles.modifiees[0];
            avis.push(
              bouge === 1 && retire === 0 && seule !== undefined
                ? `La pastille du planning a suivi : ${formaterNombre(seule.de, 2)} h → ` +
                    `${formaterNombre(seule.a, 2)} h au J${String(seule.jourRelatif)}.`
                : `Le planning a suivi : ${String(bouge)} rotation(s) recalée(s)` +
                    (retire > 0 ? `, ${String(retire)} retirée(s)` : "") +
                    ". Le détail se lit sur la ligne, au planning.",
            );
            // Le reliquat ne peut pas se taire : les rotations existantes n'ont pas pu tout
            // absorber, donc la colonne H affiche encore un écart. Sans cette phrase, on
            // croirait la ligne réglée.
            if (Math.abs(Number(pastilles.reliquat)) >= 0.005) {
              avis.push(
                `Il reste ${formaterNombre(String(Math.abs(Number(pastilles.reliquat))), 2)} h ` +
                  (Number(pastilles.reliquat) > 0
                    ? "à poser : ajouter une rotation au planning."
                    : "en trop : les rotations sont au maximum, en retirer une au planning."),
              );
            }
          }
          if (recale?.barreAjustee != null) {
            avis.push(
              `La barre du planning passe de ${String(recale.barreAjustee.de)} à ` +
                `${String(recale.barreAjustee.a)} demi-journée(s) — aucun euro n'a bougé.`,
            );
          }
          if (avis.length > 0) setAvisSaisie(avis.join(" "));
          return chargerVersion(versionId);
        })
        .then((reponse) => {
          setVersion({ phase: "chargee", reponse });
          // La fenêtre du planning regarde la même version : elle doit voir ce qui vient de
          // changer sans être fermée. Signalé APRÈS la réponse du serveur, jamais avant —
          // annoncer une écriture qui échoue ferait relire l'autre fenêtre pour rien.
          signalerVersionModifiee(versionId);
        })
        .catch((erreur: unknown) => {
          setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
        })
        .finally(() => {
          setEnAttente(null);
        });
    },
    [versionId],
  );

  /**
   * Déplacer un bloc de la contre-étude (23/09/2026).
   *
   * Séparé d'`appliquer` — qui porte déjà neuf gestes — parce que celui-ci est le seul à
   * écrire le RATTACHEMENT : tout le reste modifie des montants, des heures ou des statuts
   * sur un arbre qui ne bouge pas. Il suit en revanche exactement le même chemin de sortie :
   * relire la version au serveur plutôt que recalculer localement, puis signaler à la
   * fenêtre du planning — qui affiche le même arbre et doit le voir bouger sans être fermée.
   */
  const deplacer = useCallback(
    (bloc: string, pose: { parent: string | null; avant: string | null }): void => {
      setEnAttente(`${bloc}#0`);
      setErreurSaisie(null);
      setAvisSaisie(null);

      deplacerUnBloc(versionId, bloc, pose)
        .then((fait) => {
          /*
            **L'écart au fichier des chapitres traversés vient de changer, et il faut le dire.**

            Le montant annoncé suit le bloc — décision de Chris : « si je bouge un bloc les
            chiffres doivent suivre ». Le total de la version est intact, mais l'écart d'un
            chapitre peut passer de 22 000 € à 8 000 € sans que personne n'ait corrigé un
            prix. Sans cette phrase, ça se lirait comme une correction.
          */
          if (fait.ancetresCorriges.length > 0) {
            setAvisSaisie(
              `Le poste ${fait.bloc} est déplacé avec tout son contenu, sans changer de numéro. ` +
                `${formaterNombre(fait.montantDeplace, 2)} € de montant annoncé ont suivi : ` +
                `l'écart au fichier des chapitres ${fait.ancetresCorriges.join(", ")} a été ` +
                "recalculé. Le total du déboursé n'a pas bougé.",
            );
          }
          return chargerVersion(versionId);
        })
        .then((reponse) => {
          setVersion({ phase: "chargee", reponse });
          signalerVersionModifiee(versionId);
        })
        .catch((erreur: unknown) => {
          setErreurSaisie(erreur instanceof Error ? erreur.message : String(erreur));
        })
        .finally(() => {
          setEnAttente(null);
        });
    },
    [versionId],
  );

  const chantiers = liste.phase === "chargee" ? liste.chantiers : [];
  const chantier = chantiers.find((c) => c.id === chantierId);
  const choix = choixDuChantier(chantier);

  /** Ouvrir la contre-étude qu'on vient d'importer. */
  const ouvrir = useCallback(
    (nouveauChantierId: string, nouvelleVersionId: string): void => {
      setChantierId(nouveauChantierId);
      setVersionId(nouvelleVersionId);
      setEcran("contre-etude");
      // La liste vient peut-être d'arriver sans ce chantier : la relire réaligne
      // les listes de choix sur ce que l'écran affiche déjà.
      rechargerChantiers();
    },
    [rechargerChantiers],
  );

  return (
    <main className={ECRANS_DU_TERRAIN.has(ecran) ? "terrain" : undefined}>
      <header className="entete">
        <h1>{TITRES[ecran]}</h1>
        {/*
          **Le menu se replie sur téléphone** (Chris, 16/09/2026 : « l'écran n'a pas l'air
          responsive pour mobile »).

          Mesuré à 390 px : les dix-neuf entrées occupent huit rangées, soit tout le premier
          écran. On faisait défiler une page entière de boutons avant d'atteindre le premier
          champ à remplir — techniquement sans débordement, et parfaitement inutilisable.

          Le bouton et l'état vivent ici, mais **c'est le CSS qui décide** : au-dessus de 900 px
          le bouton disparaît et la barre s'affiche toujours, quel que soit l'état. Un repli
          piloté par une largeur lue en JavaScript se serait trompé au premier changement
          d'orientation, et aurait caché le menu sur un PC au premier rechargement mal calé.

          Le titre de l'écran reste visible en permanence : c'est lui qui dit où l'on est quand
          le menu est fermé.
        */}
        <button
          type="button"
          className="entete__bascule"
          aria-expanded={menuOuvert}
          onClick={() => {
            setMenuOuvert(!menuOuvert);
          }}
        >
          {menuOuvert ? "✕ Fermer le menu" : "☰ Menu"}
        </button>
        <nav
          className={`menu${menuOuvert ? "" : " menu--replie"}`}
          // Refermer après un choix : sur téléphone, laisser le menu ouvert repousserait
          // l'écran choisi sous la ligne de flottaison, exactement le défaut qu'on corrige.
          onClick={() => {
            setMenuOuvert(false);
          }}
        >
          {/*
            **L'ordre du menu suit le déroulé du travail** (demande de Chris,
            16/08/2026) : on importe un déboursé, on le contre-étudie, on le planifie,
            on regarde l'ensemble, on soumet à validation. Viennent ensuite les écrans
            qu'on ouvre pour administrer — les chantiers, les regroupements, les
            ressources —, et non pour avancer sur un chantier.

            Les chantiers étaient en tête comme « porte d'entrée du produit » : dans
            l'usage, on y passe une fois par semaine, alors qu'un import ouvre chaque
            nouvelle affaire.

            **Trois déplacements demandés par Chris le 21/09/2026**, et ils précisent le
            déroulé plutôt qu'ils ne le changent : les soumissions remontent juste après
            les tableaux de bord — c'est le même geste de responsable, je regarde ce qui
            m'attend puis je réponds —, et le stock puis le SAV descendent après la
            saisie chauffeur, avec les autres écrans du terrain. Le bloc va donc
            désormais du chiffrage à la validation, puis du terrain, puis de
            l'administration.
          */}
          <button
            type="button"
            className={ecran === "import" ? "actif" : undefined}
            onClick={() => {
              setEcran("import");
            }}
          >
            Importer un déboursé
          </button>
          <button
            type="button"
            className={ecran === "contre-etude" ? "actif" : undefined}
            onClick={() => {
              setEcran("contre-etude");
            }}
          >
            Contre-étude
          </button>
          <button
            type="button"
            className={ecran === "planning" ? "actif" : undefined}
            onClick={() => {
              setEcran("planning");
            }}
          >
            Planning
          </button>
          {/* Le planning général et les tableaux de bord sont ouverts à tous : le serveur
              borne la liste des chantiers au périmètre du compte, donc un conducteur y voit
              les siens et rien d'autre. Les fermer aurait privé les conducteurs de la seule
              vue qui montre comment leurs chantiers s'articulent. */}
          {voitLeMenu("plan-general") && (
            <button
              type="button"
              className={ecran === "plan-general" ? "actif" : undefined}
              onClick={() => {
                setEcran("plan-general");
              }}
            >
              Planning général
            </button>
          )}
          {voitLeMenu("tableaux-de-bord") && (
            <button
              type="button"
              className={ecran === "tableaux-de-bord" ? "actif" : undefined}
              onClick={() => {
                setEcran("tableaux-de-bord");
              }}
            >
              Tableaux de bord
            </button>
          )}
          {/* **Juste après les tableaux de bord** (Chris, 16/08/2026, replacé ici le 21/09/2026) :
              c'est le même geste de responsable — je regarde ce qui m'attend, je réponds. Ouvert
              à tous : chacun y voit ce qui le concerne, et le serveur borne la liste en SQL. */}
          {voitLeMenu("soumissions") && (
            <button
              type="button"
              className={ecran === "soumissions" ? "actif" : undefined}
              onClick={() => {
                setEcran("soumissions");
              }}
            >
              Soumissions
            </button>
          )}
          {/*
            **La saisie des heures, demandée par Chris le 16/09/2026.**

            Placée ici parce que l'ordre du menu suit le déroulé du travail : on chiffre, on
            planifie, on affecte — puis le chantier déclare ce qu'il a fait. Ouverte à tous : le
            serveur ne rend que les chantiers où la personne est réellement affectée le jour
            demandé, et un compte qui n'est sur aucun chantier y voit un écran qui le dit.

            **Un seul écran pour les trois rôles** (chef d'équipe, chef de chantier, conducteur
            d'engin) plutôt que trois sous-menus : le geste est le même, et ce qui change se
            déduit du compte. Trois entrées vers le même écran auraient obligé chacun à choisir
            son titre avant de travailler.
          */}
          {voitLeMenu("saisie-heures") && (
            <button
              type="button"
              className={ecran === "saisie-heures" ? "actif" : undefined}
              onClick={() => {
                setEcran("saisie-heures");
              }}
            >
              Saisie des heures
            </button>
          )}
          {/*
            **La validation des heures, demandée par Chris le 17/09/2026.**

            Juste après la saisie, parce que c'est la suite du même geste : le chantier déclare,
            puis la journée monte — conducteur, assistante référente, RH. Ouverte à tous, comme
            les soumissions : le serveur ne rend que ce qui attend la validation DE CE COMPTE, et
            quelqu'un qui n'est dans aucun circuit y voit une liste vide qui le dit.
          */}
          {voitLeMenu("validation-heures") && (
            <button
              type="button"
              className={ecran === "validation-heures" ? "actif" : undefined}
              onClick={() => {
                setEcran("validation-heures");
              }}
            >
              Validation des heures
            </button>
          )}
          {/*
            **L'application du chauffeur, à côté de celle du chef** (Chris, 16/09/2026).

            Un écran à part et non un onglet du précédent : ce n'est pas la même journée. Le chef
            déclare des demi-journées le soir, le chauffeur pointe des voyages au fil de l'eau — et
            il ne voit que les matériaux que NOS camions vont chercher, là où le chef confirme
            aussi ce qu'un fournisseur apporte.
          */}
          {voitLeMenu("chauffeur") && (
            <button
              type="button"
              className={ecran === "chauffeur" ? "actif" : undefined}
              onClick={() => {
                setEcran("chauffeur");
              }}
            >
              Saisie chauffeur
            </button>
          )}
          {/* **Le stock APRÈS la saisie chauffeur** (Chris, 21/09/2026). Il était à côté de la
              contre-étude depuis le 07/09 ; il rejoint les écrans du terrain, dont il est un —
              c'est le même écran que celui du magasinier, simplement servi ici dans l'application
              complète. */}
          {voitLeMenu("stocks") && (
            <button
              type="button"
              className={ecran === "stocks" ? "actif" : undefined}
              onClick={() => {
                setEcran("stocks");
              }}
            >
              Stocks
            </button>
          )}
          {/*
            **UNE seule entrée pour tout le SAV** (demande de Chris, 21/09/2026 : « tu dois créer
            SAV comme stock, puis à l'intérieur tu mettras remonter incident et autres menus par
            la suite »). C'est la forme que le module de stock a prise le 15/09, et pour les mêmes
            raisons — les onglets vivent dans `Sav.tsx`. **Placé juste après le stock**, à sa
            demande du même jour.

            Ouvert à TOUS, et c'est le point de ce module : un mécano d'atelier ou un chef
            d'équipe n'est nommé sur aucun chantier, donc son périmètre est vide et tous les
            autres écrans lui sont muets. Celui-ci ne dépend d'aucun chantier — il part de la
            machine qu'il a sous les yeux. Le serveur borne ce que chacun voit et peut écrire.
          */}
          {voitLeMenu("sav") && (
            <button
              type="button"
              className={ecran === "sav" ? "actif" : undefined}
              onClick={() => {
                setEcran("sav");
              }}
            >
              SAV
            </button>
          )}
          <button
            type="button"
            className={ecran === "chantiers" ? "actif" : undefined}
            onClick={() => {
              setEcran("chantiers");
            }}
          >
            Chantiers
          </button>
          {/*
            **UNE seule entrée pour les huit référentiels** (demande de Chris, 21/09/2026 :
            « sur écran accueil je voudrais que tu crées un menu paramétrage dans lequel tu
            pourras mettre regroupements, ressources génériques, articles, paramétrage
            transport, distance, parc matériel, associations salarié matériel, calendrier »).

            C'est la forme du module de stock et celle du SAV, pour la troisième fois — et ici
            elle répare un défaut mesuré : **la barre de menu débordait sous 1440 px**. Ces huit
            entrées sont justement celles qu'on ouvre pour administrer, jamais pour avancer sur
            un chantier ; l'ordre du menu suit le déroulé du travail, elles en étaient la queue.

            Les onglets, leur ordre et les trois réservés à l'administrateur vivent dans
            `Parametrage.tsx`. Ouverte à TOUS : cinq des huit onglets l'étaient, dont le parc
            matériel, délibérément — un mécano d'atelier n'est nommé sur aucun chantier.

            **Les chantiers et les membres restent dehors** : Chris n'a nommé ni l'un ni
            l'autre, on n'ouvre pas les chantiers pour paramétrer, et les membres sont l'écran
            qu'on veut trouver sans le chercher le jour où quelqu'un ne peut plus se connecter.
          */}
          {voitLeMenu("parametrage") && (
            <button
              type="button"
              className={ecran === "parametrage" ? "actif" : undefined}
              onClick={() => {
                setEcran("parametrage");
              }}
            >
              Paramétrage
            </button>
          )}
          {moi.role === "admin" && (
            <button
              type="button"
              className={ecran === "membres" ? "actif" : undefined}
              onClick={() => {
                setEcran("membres");
              }}
            >
              Membres
            </button>
          )}
        </nav>
        <div className="entete__compte">
          <span className="note">
            {moi.prenom} {moi.nom}
          </span>
          <button type="button" className="lien" onClick={onDeconnexion}>
            Se déconnecter
          </button>
        </div>
      </header>

      {ecran === "chantiers" && liste.phase === "chargement" && <p>Chargement des chantiers…</p>}
      {ecran === "chantiers" && liste.phase === "erreur" && (
        <p className="alerte">{liste.raison}</p>
      )}
      {ecran === "chantiers" && liste.phase === "chargee" && (
        <Chantiers
          chantiers={chantiers}
          baremes={liste.baremes}
          sitesSansTableDeDistances={liste.sitesSansTableDeDistances}
          onModifie={rechargerChantiers}
          onOuvrir={(id, cible) => {
            // Ouvrir depuis la liste, c'est choisir le chantier ET l'écran d'un seul
            // geste. La version retenue est la première du chantier — la même que
            // celle qu'aurait donnée le sélecteur, pour que les deux chemins
            // n'aboutissent pas à deux versions différentes.
            setChantierId(id);
            const suivant = chantiers.find((c) => c.id === id);
            setVersionId(choixDuChantier(suivant)[0]?.version.id ?? "");
            setEcran(cible);
          }}
        />
      )}

      {ecran === "import" && <Import onOuvrir={ouvrir} onImporte={rechargerChantiers} />}

      {ecran === "parametrage" && (
        <Parametrage role={moi.role} codeArticle={codeArticle} codeMateriel={codeMateriel} />
      )}

      {ecran === "plan-general" && (
        <PlanGeneral
          onOuvrirChantier={(id) => {
            // Ouvrir depuis la frise, c'est le même geste que depuis la liste : on choisit
            // le chantier ET l'écran. La version retenue est la première du chantier, pour
            // que les deux chemins n'aboutissent pas à deux versions différentes.
            setChantierId(id);
            const suivant = chantiers.find((c) => c.id === id);
            setVersionId(choixDuChantier(suivant)[0]?.version.id ?? "");
            setEcran("planning");
          }}
        />
      )}

      {ecran === "tableaux-de-bord" && (
        <TableauxDeBord
          // Le responsable ouvre son propre tableau : il n'a pas à cliquer sur le sien
          // chaque matin. Les autres arrivent sur l'exploitation, la vue la plus large.
          ongletInitial={(fonctions[0] as Fonction | undefined) ?? "exploitation"}
          peutAffecter={moi.role === "admin" || fonctions.length > 0}
          // L'administrateur et le responsable exploitation, eux seuls (Chris, 16/09/2026) :
          // ce tableau dit ce qu'il faut corriger avant que les saisies ne partent, ce n'est
          // pas un outil de conducteur. Le serveur ferme la route, ceci ne masque que l'onglet.
          peutVoirLesAnomalies={moi.role === "admin" || fonctions.includes("exploitation")}
          cible={cibleDuPlan}
          /*
            Le second maillon : du plan de charge à la contre-étude en mode planning.

            **La version retenue est celle du BESOIN**, et non la première du chantier comme
            partout ailleurs. Ici la différence compte : le plan de charge est calculé sur une
            version précise — la CE de suivi quand elle existe —, et ouvrir la première
            afficherait une autre ligne, ou aucune, sous le même numéro de poste. C'est
            exactement l'écart qu'on vient remonter : il faut arriver sur la ligne qui a produit
            ce qu'on regardait.
          */
          onOuvrirLaLigne={(besoin) => {
            setChantierId(besoin.chantierId);
            setVersionId(besoin.versionId);
            setCibleDeLaLigne({ poste: besoin.poste, rang: besoin.rang });
            setEcran("planning");
          }}
        />
      )}

      {/* La liste des chantiers est rechargée après chaque réponse : accepter côté exploitation
          FIGE la version et crée la CE de suivi, et sans ce rappel la liste déroulante des
          versions gardait l'état d'avant. Chris a cru que la CE de suivi n'était pas créée —
          elle l'était, elle n'apparaissait qu'après un geste qui rechargeait la liste par
          ailleurs. Une réponse qui change l'arbre des versions doit rafraîchir ce qui l'affiche. */}
      {ecran === "soumissions" && (
        <Soumissions monId={moi.id} onRepondu={rechargerChantiers} />
      )}

      {ecran === "saisie-heures" && <SaisieHeures cible={cibleDeSaisie} />}

      {/*
        **La pile du valideur** (Chris, 17/09/2026). « Ouvrir la journée » change d'écran ET de
        journée : deux états qui vivent ici, comme les deux sauts du planning. La cible n'est pas
        effacée à l'arrivée — l'écran de saisie s'en sert aussi pour son rechargement après
        enregistrement, et l'oublier y aurait ramené la journée du jour, donc un écran vide.
      */}
      {ecran === "validation-heures" && (
        <ValidationHeures
          ouvrirLaJournee={(cible) => {
            setCibleDeSaisie(cible);
            setEcran("saisie-heures");
          }}
        />
      )}

      {ecran === "chauffeur" && <SaisieChauffeur />}

      {ecran === "membres" && <Membres moi={moi} />}

      {ecran === "sav" && <Sav />}
      {ecran === "stocks" && <Stocks />}

      {ecran === "contre-etude" && (
        <p className="etat">
          Le déboursé importé, à comparer au classeur d'origine. Déplier un ouvrage pour
          retravailler ses quantités et ses prix.
        </p>
      )}

      {(ecran === "contre-etude" || ecran === "planning") && liste.phase === "chargement" && (
        <p>Chargement des chantiers…</p>
      )}

      {(ecran === "contre-etude" || ecran === "planning") && liste.phase === "erreur" && (
        <p className="alerte">{liste.raison}</p>
      )}

      {(ecran === "contre-etude" || ecran === "planning") && liste.phase === "chargee" && chantiers.length === 0 && (
        <p className="alerte">
          {/*
            Une liste vide ne veut pas dire la même chose selon qui regarde, et la confondre
            enverrait un conducteur importer un déboursé alors que le chantier existe déjà — il
            ne lui est simplement pas ouvert. Un écran vide alors que la base est pleine se lit
            comme une panne : il faut donc qu'il dise laquelle des deux situations c'est.
          */}
          {moi.role === "admin" ? (
            <>
              Aucun chantier en base.{" "}
              <button
                type="button"
                className="lien"
                onClick={() => {
                  setEcran("import");
                }}
              >
                Importer un déboursé
              </button>{" "}
              pour commencer.
            </>
          ) : (
            <>
              Aucun chantier ne vous est ouvert : vous n'êtes nommé dans l'équipe d'aucun
              chantier. Demandez à y être ajouté.
            </>
          )}
        </p>
      )}

      {(ecran === "contre-etude" || ecran === "planning") && liste.phase === "chargee" && chantiers.length > 0 && (
        <section className="selection">
          <label>
            Chantier
            <select
              value={chantierId}
              onChange={(evenement) => {
                setChantierId(evenement.target.value);
                const suivant = chantiers.find((c) => c.id === evenement.target.value);
                setVersionId(choixDuChantier(suivant)[0]?.version.id ?? "");
              }}
            >
              <option value="">— choisir —</option>
              {chantiers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.apo} — {c.nom}
                </option>
              ))}
            </select>
          </label>

          <label>
            Version
            <select
              value={versionId}
              onChange={(evenement) => {
                setVersionId(evenement.target.value);
              }}
              disabled={choix.length === 0}
            >
              {choix.length === 0 && <option value="">— aucune —</option>}
              {choix.map((c) => (
                <option key={c.version.id} value={c.version.id}>
                  {libelleChoix(c)}
                </option>
              ))}
            </select>
          </label>

          {chantier !== undefined && (
            <p className="etat">
              Statut : <strong>{libelleStatut(chantier.statut)}</strong>
              {chantier.client !== null && <> · client {chantier.client}</>}
            </p>
          )}

          {/* La création d'une version vit ici, à côté du choix de la version :
              c'est le même geste de navigation — « laquelle je regarde », puis
              « j'en veux une autre ». La placer près du déboursé la mêlerait aux
              modifications de lignes, qui n'ont pas la même portée. */}
          <NouvelleVersion
            sources={choix.map((c) => ({ id: c.version.id, libelle: libelleChoix(c) }))}
            sourceParDefaut={versionId}
            onCreee={(nouvelleVersionId) => {
              // La liste des versions est périmée dès cet instant : on la relit,
              // puis on ouvre la nouvelle. Sans le rechargement, elle n'existerait
              // pas dans la liste de choix et le sélecteur afficherait « aucune ».
              rechargerChantiers();
              setVersionId(nouvelleVersionId);
            }}
          />
          {/* Supprimer la version affichée : le ménage des variantes qui n'ont pas servi.
              N'apparaît que sur ce qui est supprimable — une version modifiable qui n'est pas
              la CE de suivi. Le serveur refuse le reste de toute façon, et par des règles :
              masquer le bouton n'est qu'un confort d'affichage. */}
          {(() => {
            const courante = choix.find((c) => c.version.id === versionId);
            if (courante === undefined) return null;
            if (courante.version.figeeLe !== null) return null;
            if (courante.version.type === "ce_suivi") return null;
            return (
              <SupprimerVersion
                versionId={versionId}
                nom={libelleChoix(courante)}
                onSupprimee={(bilan) => {
                  setAvisSaisie(bilan);
                  // La version affichée n'existe plus : on relit la liste et on bascule sur
                  // ce qui reste, sinon l'écran resterait sur un identifiant mort et
                  // afficherait « version inconnue » sans qu'on comprenne pourquoi.
                  const restantes = choix.filter((c) => c.version.id !== versionId);
                  setVersionId(restantes[0]?.version.id ?? "");
                  rechargerChantiers();
                }}
              />
            );
          })()}
          {/* Annuler la validation : la copie de travail est rendue, la CE de suivi part.
              N'apparaît que sur l'une des deux, et seulement pour l'administrateur — le geste
              retire le chantier du plan de charge et emporte les affectations réelles.
              Le serveur le revérifie, masquer un bouton n'est qu'un confort d'affichage. */}
          {moi.role === "admin" &&
            (() => {
              const courante = choix.find((c) => c.version.id === versionId);
              if (courante === undefined || chantier === undefined) return null;
              const type = courante.version.type;
              if (type !== "ce_initiale" && type !== "ce_suivi") return null;
              return (
                <AnnulerValidation
                  versionId={versionId}
                  apo={chantier.apo}
                  onAnnulee={(bilan, versionRendue) => {
                    setAvisSaisie(bilan);
                    // On ouvre la copie RENDUE, et non « ce qui reste ». C'est elle que Chris
                    // vient chercher : rester sur la CE de suivi afficherait un identifiant
                    // mort, et retomber sur le devis figé donnerait à croire qu'il ne reste
                    // que lui — l'écran vu le 07/09/2026.
                    setVersionId(versionRendue);
                    rechargerChantiers();
                  }}
                />
              );
            })()}
        </section>
      )}

      {(ecran === "contre-etude" || ecran === "planning") && version.phase === "chargement" && (
        <p>Chargement du déboursé…</p>
      )}
      {(ecran === "contre-etude" || ecran === "planning") && version.phase === "erreur" && (
        <p className="alerte">{version.raison}</p>
      )}
      {ecran === "planning" && version.phase === "chargee" && (
        <Planning
          entete={version.reponse.version}
          depo={version.reponse.depo}
          // Le devis figé sert d'ancre aux heures : sans lui, un étirement passé est
          // indétectable à l'écran (la proposition se recale sur les heures étirées).
          reference={version.reponse.reference}
          versionId={versionId}
          carrieres={carrieres}
          // Les attelages proposables par poste (076), groupes par le serveur d apres les
          // couplages du Parametrage transport : la pastille de tonnage y designe le sien.
          attelagesDesPostes={version.reponse.attelages}
          transportsFiges={version.reponse.transportsFiges}
          modifiable={version.reponse.modifiable}
          // La ligne sur laquelle arriver quand on vient du plan de charge (17/09/2026).
          cible={cibleDeLaLigne}
          onChange={() => {
            // Le planning a pu changer le DEBOURSE : on relit tout, pas seulement
            // les barres. Recharger la seule frise laisserait des totaux perimes
            // a l ecran, et c est precisement ce qu il ne faut pas ici.
            chargerVersion(versionId)
              .then((reponse) => {
                setVersion({ phase: "chargee", reponse });
                // Et l'autre fenêtre, s'il y en a une : le planning de l'écran et celui de la
                // fenêtre sont le même planning.
                signalerVersionModifiee(versionId);
              })
              .catch(() => undefined);
          }}
        />
      )}

      {ecran === "contre-etude" && version.phase === "chargee" && (
        <>
          <p className="etat provenance">
            {version.reponse.version.fichierNom !== null && (
              <>
                Importé du fichier <code>{version.reponse.version.fichierNom}</code>.{" "}
              </>
            )}
            {version.reponse.modifiable
              ? "Cette version est modifiable."
              : "Cette version est figée : son contenu ne peut plus changer."}
            {" "}
            {/*
              **Ouvrir le planning sur un deuxième écran** *(Chris, 22/09/2026)*.

              Une vraie fenêtre et non un onglet, comme pour le plan de charge : un onglet ne se
              regarde pas en même temps que celui qu'on quitte, ce qui est tout l'objet de la
              demande. Les deux fenêtres se tiennent au courant l'une de l'autre — voir
              `synchroVersion.ts`.

              Affiché même sur une version figée : on regarde le planning d'une contre-étude
              validée aussi souvent qu'on le modifie, et la fenêtre sait se passer des champs
              de saisie (`modifiable` traverse comme partout ailleurs).
            */}
            <button
              type="button"
              className="lien"
              onClick={() => {
                window.open(
                  adresseDuPlanningChantier(versionId),
                  nomFenetrePlanning(versionId),
                  "popup=yes,width=1500,height=900",
                );
              }}
            >
              Ouvrir le planning dans une fenêtre
            </button>
          </p>

          {/*
            L'équipe, au-dessus du déboursé : c'est une propriété du CHANTIER, pas une
            ligne de chiffrage. Elle reste donc affichée et modifiable même sur une
            version figée — figer un chiffrage ne fige pas les gens.
          */}
          <Equipe chantierId={version.reponse.version.chantierId} generiques={generiques} />

          {/* Le numéro de version, les validations et l'historique des totaux : ce qui répond à
              « où en est cette contre-étude ? ». Replié par défaut — le geste quotidien est de
              travailler les lignes, pas de soumettre. */}
          <Suivi versionId={versionId} modifiable={version.reponse.modifiable} />

          {erreurSaisie !== null && (
            <p className="alerte" role="alert">
              {erreurSaisie}
            </p>
          )}

          {/* Une pastille apparue sans clic : elle se dit, elle ne se découvre pas. */}
          {avisSaisie !== null && <p className="etat">{avisSaisie}</p>}

          <Debourse
            entete={version.reponse.version}
            depo={version.reponse.depo}
            reference={version.reponse.reference}
            libelleReference={version.reponse.libelleReference}
            modifiable={version.reponse.modifiable}
            enAttente={enAttente}
            generiques={generiques}
            groupes={groupes}
            carrieres={carrieres}
            /* Où le panneau de stock s'ouvre (070). Les codes viennent du serveur, qui seul
               juge si un code est celui d'un article — avec le préfixe paramétré. */
            stock={{
              chantierId: version.reponse.version.chantierId,
              codes: new Set(version.reponse.articlesDuStock.map((a) => a.code)),
            }}
            onDeplacer={deplacer}
            onModifier={appliquer}
          />
        </>
      )}
    </main>
  );
}

/* ---------------------------------------------------------------------------
 * Le portier
 * ------------------------------------------------------------------------- */

/**
 * Ce que l'URL demande, avant toute question d'identité.
 *
 * Deux chemins doivent rester atteignables SANS être connecté — c'est tout leur objet :
 * `/inscription/<jeton>` sert à créer son compte, `/reinitialiser/<jeton>` à retrouver son accès.
 * Les mettre derrière la connexion serait un cercle vicieux.
 *
 * Lu depuis `window.location` plutôt que par un routeur : l'application n'en a pas, et en ajouter
 * un pour deux chemins publics serait une dépendance et un remaniement pour rien. À revoir le
 * jour où les écrans de chantier auront eux aussi des URL propres.
 */
function cheminPublic(): { page: "inscription" | "reinitialiser"; jeton: string } | null {
  const chemin = window.location.pathname;
  const inscription = /^\/inscription\/(.+)$/.exec(chemin);
  if (inscription?.[1] !== undefined) return { page: "inscription", jeton: inscription[1] };
  const reinitialisation = /^\/reinitialiser\/(.+)$/.exec(chemin);
  if (reinitialisation?.[1] !== undefined) {
    return { page: "reinitialiser", jeton: reinitialisation[1] };
  }
  return null;
}

type EtatAcces =
  | { phase: "chargement" }
  | { phase: "anonyme" }
  | { phase: "connecte"; compte: Compte; fonctions: readonly string[] };

/**
 * La racine : elle décide QUI voit quoi, et rien d'autre.
 *
 * Trois états seulement, et le premier compte : tant qu'on ne sait pas si quelqu'un est connecté,
 * on n'affiche **ni** l'application **ni** la page de connexion. Afficher la connexion pendant la
 * vérification ferait clignoter un formulaire à chaque rechargement de page pour quelqu'un qui
 * est déjà connecté.
 */
export function App(): JSX.Element {
  const [etat, setEtat] = useState<EtatAcces>({ phase: "chargement" });
  const publique = cheminPublic();

  useEffect(() => {
    // Les pages publiques n'interrogent pas la session : elles s'ouvrent sur un lien reçu par
    // courriel, souvent sur un autre appareil que celui où l'on travaille.
    if (publique !== null) return;
    chargerMonCompte()
      .then((r) => {
        setEtat(
          r.compte === null
            ? { phase: "anonyme" }
            : { phase: "connecte", compte: r.compte, fonctions: r.fonctions },
        );
      })
      .catch(() => {
        // Un serveur muet n'est pas une session absente : on affiche quand même la connexion,
        // qui dira ce qui ne va pas au premier essai. Rien d'autre n'est utilisable de toute façon.
        setEtat({ phase: "anonyme" });
      });
  }, [publique === null]);

  if (publique !== null) {
    return publique.page === "inscription" ? (
      <Inscription jeton={publique.jeton} />
    ) : (
      <NouveauMotDePasse jeton={publique.jeton} />
    );
  }

  if (etat.phase === "chargement") {
    return (
      <main className="acces">
        <section className="acces__panneau">
          <p>Vérification de la session…</p>
        </section>
      </main>
    );
  }

  if (etat.phase === "anonyme") {
    return (
      <Connexion
        onConnecte={(compte) => {
          // La connexion rend le compte, pas les fonctions de validation : on entre sans
          // elles, puis on relit. C'est un aller-retour de plus, et il évite de faire
          // porter à la route de connexion une information qui n'y sert à rien — un
          // tableau de bord ouvert une seconde sur le mauvais onglet est sans gravité.
          setEtat({ phase: "connecte", compte, fonctions: [] });
          chargerMonCompte()
            .then((r) => {
              if (r.compte !== null) {
                setEtat({ phase: "connecte", compte: r.compte, fonctions: r.fonctions });
              }
            })
            .catch(() => undefined);
        }}
      />
    );
  }

  /*
    **La fenêtre satellite du plan de charge, et rien autour.**

    Elle s'ouvre à côté du tableau de bord (Chris, 03/09/2026 : « une fenêtre supplémentaire
    simultanément à sa vue du plan de charge »), donc elle n'affiche ni menu ni titre
    d'application : la place sert à la grille, et un menu inviterait à naviguer dans une fenêtre
    qui n'existe que pour être regardée. Elle passe par la même vérification de session que
    l'application — c'est le même cookie, sur la même origine.

    Reconnue ici plutôt que dans `cheminPublic` : il faut être connecté pour la voir. Et après
    la connexion, non avant, sinon un lien ouvert sur une session expirée afficherait une grille
    vide au lieu du formulaire.
  */
  if (window.location.pathname === CHEMIN_FENETRE) return <PlanningRessources />;

  /*
    **La fenêtre du planning d'une contre-étude** *(Chris, 22/09/2026)*.

    Reconnue au même endroit et pour les mêmes raisons que celle du plan de charge : après la
    vérification de session, parce qu'il faut être connecté pour la voir, et avant tout écran,
    parce qu'elle n'en est pas un — c'est une fenêtre à part, sans menu.

    Les deux fenêtres satellites peuvent être ouvertes en même temps : elles ne regardent pas la
    même chose. Celle-ci montre le planning d'UNE contre-étude, celle du dessus le chargement
    des ressources du groupe.
  */
  if (window.location.pathname === CHEMIN_FENETRE_PLANNING) return <FenetrePlanning />;
  // L'assistant de saisie (docs/assistant-planning.md) : la troisième fenêtre satellite, ouverte
  // par le logo de la fenêtre du planning et jamais depuis l'application elle-même.
  if (window.location.pathname === CHEMIN_FENETRE_ASSISTANT) return <FenetreAssistant />;

  /*
    **Le magasin est une AUTRE application, pas celle-ci avec moins de boutons.**

    `Application` charge au montage les chantiers, les génériques, les regroupements et les
    carrières — que le rôle `magasin` n'a pas le droit de lire (067). L'ouvrir avec ce compte
    n'afficherait pas « moins de boutons » mais quatre refus d'accès, avant le moindre clic.
    Masquer des boutons n'y changerait rien : ces appels partent au montage.
  */
  if (etat.compte.role === "magasin") {
    return (
      <Magasin
        moi={etat.compte}
        onDeconnexion={() => {
          void seDeconnecter().finally(() => {
            setEtat({ phase: "anonyme" });
          });
        }}
      />
    );
  }

  return (
    <Application
      moi={etat.compte}
      fonctions={etat.fonctions}
      onDeconnexion={() => {
        // L'état passe à « anonyme » quoi qu'il arrive : si la révocation échoue côté serveur, le
        // cookie peut rester — mais garder l'application affichée après un clic sur « se
        // déconnecter » serait bien plus trompeur.
        void seDeconnecter().finally(() => {
          setEtat({ phase: "anonyme" });
        });
      }}
    />
  );
}
